import {
  Button,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  LOCK_WARNING,
  ListContext,
  ListContextValue,
  LockedIcon,
  MenuItemType,
  Stack,
  Switch,
  Tooltip,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
  IconButton,
} from "@compass/core";
import ContentCopySharpIcon from '@mui/icons-material/ContentCopySharp';
import { DataModelInformation, SubmitButton } from "components";
import {
  DataModelEnum,
  Permission,
  PermissionAction,
  StudentService,
  StudentServiceAdditionalEntry,
} from "generated/openApi";
import { studentServiceIndexListPageRoute } from "pages/students/student-service-indexes/StudentServiceIndexListPage/StudentServiceIndexListPage";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const StudentServicePageHeader = (props: {
  theme?: "Dark" | "Standard";
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  service: StudentService;
  setService: (service: StudentService) => void;
  initService: StudentService;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  resetUnsavedChanges: (items: any[]) => void;
  additionalEntries: StudentServiceAdditionalEntry[];
  setAdditionalEntries: (entries: StudentServiceAdditionalEntry[]) => void;
  additionalDates: Date[];
  setAdditionalDates: (dates: Date[]) => void;
  hideStudent: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.service.id;
  const [studentName, setStudentName] = useState<string>("");
  const [createAnother, setCreateAnother] = useState<boolean>(false);
  const [createAnotherRetainData, setCreateAnotherRetainData] = useState<boolean>(false);
  const creatingMultiple = props.additionalEntries.length > 0 || props.additionalDates.length > 0;
  const creatingEntryCount = creatingMultiple
    ? (props.additionalEntries.length + 1) * (props.additionalDates.length + 1)
    : 1;
  const location = useLocation();
  const hasDialog = location.pathname.includes('dialog');

  useEffect(() => {
    if (props.service.id === 0 && props.service.studentId > 0) {
      ApiService.studentServices.getStudentName(props.service.studentId).then((response) => {
        setStudentName(response);
      });
    }
  }, [props.service.studentId]);

  const duplicate = () => {
    ApiService.studentServices.duplicate(id).then((response) => {
      props.setService(response);
      props.resetUnsavedChanges([response, props.additionalDates, props.additionalEntries]);
      props.reroute(0, "edit");
    });
  };

  const save = (service: StudentService) => {
    props.setValidationErrors({});

    if (service.id > 0) {
      ApiService.studentServices
        .saveUpdate(service)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      if (!creatingMultiple) {
        ApiService.studentServices
          .saveCreate(service)
          .then((response) => {
            const id = response;
            if (createAnother) {
              if (createAnotherRetainData) {
                props.resetUnsavedChanges([service, props.additionalDates, props.additionalEntries]);
              } else {
                props.setService(props.initService);
                props.resetUnsavedChanges([props.initService, props.additionalDates, props.additionalEntries]);
              }
            } else {
              props.reroute(id, "view");
              props.fetch(id);
            }
            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.data);
          });
      } else {
        ApiService.studentServices
          .saveCreateAdditionalEntries({
            service: service,
            additionalEntries: props.additionalEntries,
            additionalDates: props.additionalDates,
          })
          .then(() => {
            if (createAnother) {
              if (createAnotherRetainData) {
                props.resetUnsavedChanges([service, props.additionalDates, props.additionalEntries]);
              } else {
                props.setService(props.initService);
                props.setAdditionalDates([]);
                props.setAdditionalEntries([]);
                props.resetUnsavedChanges([props.initService, [], []]);
              }
            } else {
              navigate("../");
            }

            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.data);
          });
      }
    }
  };

  const toggleLock = (id: number) => {
    if (window.confirm(LOCK_WARNING)) {
      ApiService.studentServices.toggleLock(id).then((response) => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.studentServices.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const openServiceIndex = () => {
    navigate({
      pathname: studentServiceIndexListPageRoute.getPath({ screenType: "dialog" }),
      search: studentServiceIndexListPageRoute.getOptionalParams?.({
        selectMode: true,
      }),
    });
  };


  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Lock)) {
      if (!props.service.isLocked)
        menuItems.push({
          label: <Typography color="error">Lock</Typography>,
          action: () => toggleLock(id),
        });
      else
        menuItems.push({
          label: "Unlock",
          action: () => toggleLock(id),
        });
    }

    if (
      isAuthorized(props.rootPermission, PermissionAction.Delete) &&
      (!props.service.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock))
    )
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Stack>
          <Typography variant="body1">
            {id === 0 ? "New" : ""} {props.handle}
          </Typography>
          
          {id === 0 && props.hideStudent && (
            <Typography variant="inherit" lineHeight={"100%"}>
              for {studentName}
            </Typography>
          )}
        </Stack>

        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.service.isLocked && <LockedIcon />}

        <Stack>
          {id === 0 && (
            <>
              <Switch
                color="success"
                value={createAnother}
                onChange={(e) => setCreateAnother(e.target.checked)}
                label="+ more"
                size={createAnother ? "small" : undefined}
              />

              {createAnother && (
                <Tooltip title="Retain data after add">
                  <Switch
                    value={createAnotherRetainData}
                    onChange={(e) => setCreateAnotherRetainData(e.target.checked)}
                    label="Retain"
                    size="small"
                  />
                </Tooltip>
              )}
            </>
          )}
        </Stack>

        {id > 0 && <DataModelInformation color={hasDialog ? "info" : "primary"} dataModelType={DataModelEnum.StudentService} dataModelId={id} />}

        {id > 0 && props.crudMode === "view" && (
          <Tooltip title="Duplicate">
            <IconButton color={hasDialog ? "info" : "primary"} onClick={duplicate} aria-label="Duplicate service">
              <ContentCopySharpIcon />
            </IconButton>
          </Tooltip>
        )}

        {props.crudMode === "view" &&
          isAuthorized(props.rootPermission, PermissionAction.Update) &&
          (!props.service.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
            <UpdateButton onClick={() => props.reroute(id, "edit")} />
          )}

        {props.service.id === 0 && (
          <SubmitButton label="Index" theme={props.theme} onClick={() => openServiceIndex()} variant="outlined" />
        )}

        {props.crudMode === "edit" && (
          <SubmitButton
            theme={props.theme}
            onClick={() => save(props.service)}
            disabled={!props.unsavedChanges}
            label={creatingMultiple ? `Save (${creatingEntryCount} entries)` : undefined}
          />
        )}

        <CloseButton theme={props.theme} confirm={props.unsavedChanges} />

      </Stack>
    </Stack>
  );
};
